import { useEffect, useState, type FormEvent } from "react"
import { useNavigate, useParams } from "react-router"
import { getDocumentTypes } from "@alascorporation/utils"
import api from "../../lib/api"
import { getErrorMessage } from "../../lib/errors"
import { toastError } from "../../lib/toast"
import { inputCls, labelCls } from "../../components/admin/FormComponents"
import { StudentProfileEditor } from "../../components/admin/StudentProfileEditor"
import { type StudentProfileFields, type SectionId } from "../../components/admin/StudentProfileSections"
import { LoadState } from "@/components/ui/LoadState"

type UserDetail = {
  id: string
  name: string
  email: string
  phone?: string | null
  profile: StudentProfileFields | null
}

export default function StudentProfilePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [user, setUser] = useState<UserDetail | null>(null)
  const [profile, setProfile] = useState<StudentProfileFields>({})
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)
  const [activeSection, setActiveSection] = useState<SectionId>("personal")

  useEffect(() => {
    if (!id) return
    let cancelled = false
    setLoading(true)
    api
      .get(`/admin/users/${id}`)
      .then((res) => {
        if (cancelled) return
        const data = res.data.data as UserDetail
        const loadedProfile = data.profile ?? {}
        setUser(data)
        setProfile(loadedProfile)
        setEmail(data.email)
        setPhone(data.phone ?? "")
        setActiveSection("personal")
      })
      .catch(() => {
        if (!cancelled) setError("No se pudo cargar la información del estudiante.")
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id])

  function set<K extends keyof StudentProfileFields>(key: K, value: StudentProfileFields[K]) {
    setProfile((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!id) return
    setSaving(true)
    setError("")
    setSuccess(false)
    try {
      const fullName = [profile.firstName, profile.lastName].filter(Boolean).join(" ").trim()
      const payload: Record<string, unknown> = { email: email.trim(), phone: phone.trim() || null, profile }
      if (fullName) payload.name = fullName

      const res = await api.patch(`/admin/users/${id}/profile`, payload)
      setProfile(res.data.data?.profile ?? profile)
      if (res.data.data?.email) {
        setEmail(res.data.data.email)
        setUser((prev) => (prev ? { ...prev, email: res.data.data.email } : prev))
      }
      if (res.data.data?.name) {
        setUser((prev) => (prev ? { ...prev, name: res.data.data.name } : prev))
      }
      setPhone(res.data.data?.phone ?? "")
      setUser((prev) => (prev ? { ...prev, phone: res.data.data?.phone ?? null } : prev))
      setSuccess(true)
    } catch (err) {
      const message = getErrorMessage(err)
      setError(message)
      toastError(message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <LoadState />
      </div>
    )
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-3">
        <p role="alert" aria-live="assertive" className="text-sm text-error-600 dark:text-error-400">No se encontró el estudiante.</p>
        <button type="button" onClick={() => navigate("/instructor/students")} className="text-sm text-brand-500 hover:underline">
          ← Volver a estudiantes
        </button>
      </div>
    )
  }

  const documentTypes = getDocumentTypes(profile.countryCode)

  return (
    <StudentProfileEditor
      breadcrumbCurrent={user.name}
      heroTitle={user.name}
      heroSubtitleItems={[user.email, user.phone || "Sin celular registrado"]}
      profile={profile}
      set={set}
      documentTypes={documentTypes}
      activeSection={activeSection}
      onActiveSectionChange={setActiveSection}
      personalExtra={
        <>
          <div>
            <label className={labelCls}>Email</label>
            <input
              type="email"
              className={inputCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <label className={labelCls}>Número de celular</label>
            <input
              type="tel"
              className={inputCls}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Ej: 3001234567"
            />
          </div>
        </>
      }
      infoMessage="No es necesario llenarlo todo de una vez. Guarda cuando quieras — lo ya guardado no se pierde — y vuelve más adelante por el resto."
      error={error || undefined}
      successMessage={success ? "La información se actualizó correctamente." : undefined}
      onSubmit={handleSubmit}
      submitLabel={saving ? "Guardando..." : "Guardar cambios"}
      submitDisabled={saving}
      onCancel={() => navigate("/instructor/students")}
      cancelLabel="Volver"
      onBackToList={() => navigate("/instructor/students")}
    />
  )
}
